import React from 'react';
import {
  Box,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Typography
} from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import AuthLandingLayout from '../components/AuthLandingLayout';
import { customers, getCustomerWebHostname, isCustomerSelectable } from '../config/customers';
import { setSelectedCustomerId } from '../lib/selectedCustomer';
import { navigateTo, resolveCustomerAppUrl } from '../lib/runtimeUrls';
import { useThemeMode } from '../theme/ThemeModeContext';
import { accent, darkTokens, lightTokens } from '../theme/tokens';

const SelectCustomerPage = () => {
  const { mode } = useThemeMode();
  const tokens = mode === 'dark' ? darkTokens : lightTokens;

  const handleSelectCustomer = (customerId) => {
    const customer = customers.find((entry) => entry.id === customerId);
    if (!customer || !isCustomerSelectable(customer)) return;

    setSelectedCustomerId(customer.id);
    navigateTo(resolveCustomerAppUrl(customer));
  };

  return (
    <AuthLandingLayout tagline="Velg riktig kommune for å logge inn mot korrekt organisasjon.">
      <Typography
        variant="overline"
        sx={{
          display: 'block',
          mb: 1.5,
          fontWeight: 700,
          letterSpacing: '0.12em',
          color: tokens.ink70
        }}
      >
        Kommunevalg
      </Typography>

      <Typography
        variant="h4"
        component="h2"
        sx={{
          fontWeight: 700,
          letterSpacing: '-0.02em',
          color: tokens.fg,
          mb: 1
        }}
      >
        Velg kommune
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Hvilken kommune skal du logge inn i?
      </Typography>

      <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {customers.map((customer) => {
          const selectable = isCustomerSelectable(customer);

          return (
            <ListItemButton
              key={customer.id}
              disabled={!selectable}
              onClick={() => handleSelectCustomer(customer.id)}
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 2,
                bgcolor: tokens.surface,
                border: `1px solid ${tokens.border}`,
                opacity: selectable ? 1 : 0.55,
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                '&:hover': selectable
                  ? {
                      borderColor: accent.main,
                      boxShadow: `0 8px 24px -12px ${tokens.cardShadow}`
                    }
                  : undefined
              }}
            >
              <ListItemText
                primary={customer.name}
                secondary={
                  selectable
                    ? getCustomerWebHostname(customer)
                    : `${getCustomerWebHostname(customer)} · Ikke tilgjengelig ennå`
                }
                primaryTypographyProps={{ fontWeight: 600 }}
              />
              {selectable && <ChevronRight sx={{ color: accent.main }} />}
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          variant="text"
          color="inherit"
          onClick={() => {
            window.location.href = '/';
          }}
          sx={{
            fontWeight: 600,
            color: tokens.ink70,
            '&:hover': { color: accent.main, bgcolor: accent.soft }
          }}
        >
          Avbryt
        </Button>
      </Box>
    </AuthLandingLayout>
  );
};

export default SelectCustomerPage;
